import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { IMPERSONATION_END_API_TIMEOUT_MS } from '../constants/impersonationRoute.constants'
import {
  getImpersonationSession,
  setImpersonationSession,
  subscribeImpersonationSession,
} from '../helpers/impersonationToken.helper'
import { buildImpersonationMenuTargetLabel } from '../helpers/impersonationTarget.helper'

export type StartImpersonationInput =
  | { targetUserId: string }
  | { targetEmail: string }

interface TargetUser {
  id: string
  username: string
  firstName: string | null
  lastName: string | null
  /** Present when API returns it (older servers may omit). */
  email?: string | null
}

interface ImpersonateResponse {
  success: boolean
  data?: {
    token: string
    expiresAt: string
    targetUser: TargetUser
  }
  error?: string
}

function getSessionSnapshot() {
  return getImpersonationSession()
}

function buildImpersonateBody(input: StartImpersonationInput, reason?: string): Record<string, unknown> {
  if ('targetEmail' in input) {
    return { targetEmail: input.targetEmail.trim(), ...(reason ? { reason } : {}) }
  }
  return { targetUserId: input.targetUserId.trim(), ...(reason ? { reason } : {}) }
}

export function useImpersonation() {
  const apiClient = useApiClient()
  const [targetUser, setTargetUser] = useState<TargetUser | null>(null)
  const sessionSnap = useSyncExternalStore(subscribeImpersonationSession, getSessionSnapshot, getSessionSnapshot)
  const isActive = Boolean(sessionSnap?.token)
  const activeTargetDisplayName = targetUser
    ? buildImpersonationMenuTargetLabel({ ...targetUser, email: targetUser.email ?? null })
    : sessionSnap?.targetLabel ?? null
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    return subscribeImpersonationSession(() => {
      if (!getImpersonationSession()) {
        setTargetUser(null)
      }
    })
  }, [])

  const startImpersonation = useCallback(
    async (input: StartImpersonationInput, reason?: string): Promise<boolean> => {
      setError(null)
      setBusy(true)
      try {
        const res = await apiClient<ImpersonateResponse>(API_ENDPOINTS.zportiAdmin.impersonate, {
          method: 'POST',
          data: buildImpersonateBody(input, reason),
        })
        const d = res.data?.data
        if (!d?.token) throw new Error(res.data?.error || 'Impersonation failed')
        const label = buildImpersonationMenuTargetLabel({
          ...d.targetUser,
          email: d.targetUser.email ?? null,
        })
        setImpersonationSession({
          token: d.token,
          targetUserId: d.targetUser.id,
          targetLabel: label,
          expiresAt: new Date(d.expiresAt).getTime(),
        })
        setTargetUser(d.targetUser)
        return true
      } catch (e) {
        setError(extractErrorMessage(e, 'Could not start impersonation'))
        return false
      } finally {
        setBusy(false)
      }
    },
    [apiClient],
  )

  const exitImpersonation = useCallback(async () => {
    const s = getImpersonationSession()
    const tid = s?.targetUserId ?? targetUser?.id
    const token = s?.token
    if (tid && token) {
      try {
        await apiClient(API_ENDPOINTS.zportiAdmin.impersonateEnd, {
          method: 'POST',
          data: { targetUserId: tid },
          timeout: IMPERSONATION_END_API_TIMEOUT_MS,
        })
      } catch {
        /* best-effort audit close */
      }
    }
    setImpersonationSession(null)
    setTargetUser(null)
    setError(null)
  }, [apiClient, targetUser?.id])

  return {
    isActive,
    activeTargetDisplayName,
    targetUser,
    error,
    busy,
    startImpersonation,
    exitImpersonation,
  }
}
